import React, { useCallback, useEffect, useState } from "react";

import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";
import { useGetDocumentsMutation } from "../../../store/api/lumi/documents";
import SummaryContainer from "../../Summary/SummaryContainer";
import SummaryHeader from "../../Summary/SummaryHeader";
import SummaryDocuments from "../../Summary/SummaryDocuments";
import SummaryText from "../../Summary/SummaryText";

const LargestConceptMovementSummary = ({
                                           id,
                                           selectedConcept,
                                           filterBaseline,
                                           filterComparison,
                                           comparingDates
                                       }) => {
    const [triggerGetDocuments] = useGetDocumentsMutation();
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [baselineDocuments, setBaselineDocuments] = useState([]);
    const [comparisonDocuments, setComparisonDocuments] = useState([]);

    const [loadingConceptSummary, setLoadingConceptSummary] = useState(false);
    const [baselineSummary, setBaselineSummary] = useState(null);
    const [comparisonSummary, setComparisonSummary] = useState(null);

    const fetchDocuments = useCallback(async () => {
        // nothing to do if we don't have a selectedConcept or breakdown information
        if (!selectedConcept) return;

        setLoadingDocuments(true);
        try {
            const [resultBaseline, resultComparison] = await Promise.all([
                triggerGetDocuments({
                    id,
                    filter: filterBaseline,
                    search: selectedConcept.texts,
                }).unwrap(),
                triggerGetDocuments({
                    id,
                    filter: filterComparison,
                    search: selectedConcept.texts,
                }).unwrap(),
            ]);

            setBaselineDocuments(resultBaseline.documents);
            setComparisonDocuments(resultComparison.documents);
        } catch (error) {
            console.error("Failed to fetch documents:", error);

            setBaselineDocuments([]);
            setComparisonDocuments([]);
        } finally {
            setLoadingDocuments(false);
        }
    }, [triggerGetDocuments, id, selectedConcept]);

    useEffect(() => {
        fetchDocuments();
    }, [selectedConcept]);

    const handleLlmRequest = useCallback(
        async (prompt) => {
            try {
                const llmResponse = await triggerGetLLMPassThrough({
                    projectId: id,
                    prompt: prompt,
                }).unwrap();

                return llmResponse.response;
            } catch (error) {
                console.error("failed to fetch summary", error);
                return null;
            }
        },
        [id, prompt],
    );

    const fetchSummary = useCallback(async () => {
        if (!selectedConcept) return;
        if (baselineDocuments.length === 0 || comparisonDocuments.length === 0) return;

        setLoadingConceptSummary(true);
        try {
            const baselinePrompt =
                `Summarize how "${selectedConcept.texts.join(", ")}" is spoken about in these documents\n` +
                baselineDocuments.map((d) => `<p>${d.text}</p>`).join("");

            const comparisonPrompt =
                `Analyze and summarize the differences in how often "${selectedConcept.texts.join(", ")}" is mentioned or discussed between the two document sets provided below. Focus on comparing key themes, tones, contexts, and notable shifts or contrasts in the nature of mentions. Ensure the summary is structured clearly and references the specific dates of each document set for clarity.\n\n` +
                `Document Set from ${comparingDates[1]}:\n` +
                baselineDocuments
                    .slice(0, 25)
                    .map((d, index) => `${index + 1}. ${d.text}`)
                    .join("\n") +
                `\n\nDocument Set from ${comparingDates[0]}:\n` +
                comparisonDocuments
                    .slice(0, 25)
                    .map((d, index) => `${index + 1}. ${d.text}`)
                    .join("\n") +
                `\n\nPlease provide a structured comparison that emphasizes the differences between the two sets of documents in relation to "${selectedConcept.texts.join(", ")}." Use bullet points or paragraphs to highlight:\n` +
                `- Changes in the frequency of mentions.\n` +
                `- Shifts in tone, sentiment, or context.\n` +
                `- Key themes or ideas associated with the concept across the two time periods.\n` +
                `Avoid using the terms "Baseline" and "Comparison." Instead, refer to the document sets by their respective dates (${comparingDates[1]} and ${comparingDates[0]}).\n`;

            const [baselineResult, comparisonResult] = await Promise.all([
                handleLlmRequest(baselinePrompt),
                handleLlmRequest(comparisonPrompt),
            ]);

            setBaselineSummary(baselineResult);
            setComparisonSummary(comparisonResult);
        } catch (error) {
            console.error("Failed to fetch summaries:", error);
        } finally {
            setLoadingConceptSummary(false);
        }
    }, [baselineDocuments, comparisonDocuments, handleLlmRequest]);

    useEffect(() => {
        fetchSummary();
    }, [baselineDocuments]);

    if (!selectedConcept)
        return (
            <SummaryContainer>
                <SummaryHeader>
                    Select a concept to show detailed summary.
                </SummaryHeader>
            </SummaryContainer>
        );

    return (
        <>
            <SummaryText
                header="Baseline Summary"
                loading={loadingConceptSummary}
                summary={baselineSummary}
            />
            <SummaryDocuments
                header={`Baseline Documents for ${selectedConcept.name}`}
                loading={loadingDocuments}
                documents={baselineDocuments}
            />
            <SummaryText
                header="Comparison Summary"
                loading={loadingConceptSummary}
                summary={comparisonSummary}
            />
            <SummaryDocuments
                header={`Comparison Documents for ${selectedConcept.name}`}
                loading={loadingDocuments}
                documents={comparisonDocuments}
            />
        </>
    );
};

export default LargestConceptMovementSummary;
